import React, { useState, useEffect } from 'react';
import { TournamentAccessCreateInput } from '../../services/tournamentAccessService';
import { organizationsService } from '../../services/organizationsService';
import { tournamentsService } from '../../services/tournamentsService';
import SearchableDropdown from '../shared/SearchableDropdown';

interface AddTournamentAccessModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (data: TournamentAccessCreateInput) => Promise<void>;
}

interface DropdownOption {
  id: string;
  name: string;
}

const AddTournamentAccessModal: React.FC<AddTournamentAccessModalProps> = ({
  isOpen,
  onClose,
  onSave
}) => {
  const [formData, setFormData] = useState<TournamentAccessCreateInput>({
    tournament_id: '',
    organisation_id: '',
    access_type: '',
    granted_by: null,
    granted_at: new Date().toISOString(),
    is_active: true
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isSaving, setIsSaving] = useState(false);
  const [tournaments, setTournaments] = useState<DropdownOption[]>([]);
  const [organizations, setOrganizations] = useState<DropdownOption[]>([]);

  // Fetch tournaments and organizations for dropdowns
  useEffect(() => {
    const fetchDropdownData = async () => {
      try {
        const [tournamentsData, orgs] = await Promise.all([
          tournamentsService.getAll(),
          organizationsService.getAll()
        ]);
        
        const tournamentOptions = tournamentsData
          .filter(t => t.active)
          .map(tournament => ({
            id: tournament.id,
            name: `${tournament.name} (${tournament.country || 'N/A'})`
          }));
        setTournaments(tournamentOptions);
        
        const orgOptions = orgs.map(org => ({
          id: org.id,
          name: `${org.name} (${org.subdomain})`
        }));
        setOrganizations(orgOptions);
      } catch (err) {
        console.error('Error fetching dropdown data:', err);
      }
    };

    if (isOpen) {
      fetchDropdownData();
    }
  }, [isOpen]);

  const handleInputChange = (field: keyof TournamentAccessCreateInput, value: string | boolean | null) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const resetForm = () => {
    setFormData({
      tournament_id: '',
      organisation_id: '',
      access_type: '',
      granted_by: null,
      granted_at: new Date().toISOString(),
      is_active: true
    });
    setErrors({});
  };

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.tournament_id?.trim()) {
      newErrors.tournament_id = 'Tournament ID is required';
    }

    if (!formData.organisation_id?.trim()) {
      newErrors.organisation_id = 'Organisation ID is required';
    }

    if (!formData.access_type?.trim()) {
      newErrors.access_type = 'Access type is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setIsSaving(true);
    try {
      await onSave(formData);
      resetForm();
      onClose();
    } catch (error) {
      // Error handling is done in parent component
      console.error('Error creating tournament access:', error);
    } finally {
      setIsSaving(false);
    }
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900">Add Tournament Access</h2>
          <button
            onClick={handleClose}
            className="text-gray-400 hover:text-gray-600 focus:outline-none"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {/* Tournament Selection */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Tournament *
            </label>
            <SearchableDropdown
              options={tournaments}
              value={formData.tournament_id}
              onChange={(value) => handleInputChange('tournament_id', value)}
              placeholder="Search tournaments..."
              label=""
            />
            {errors.tournament_id && (
              <p className="text-red-500 text-xs mt-1">{errors.tournament_id}</p>
            )}
          </div>

          {/* Organisation Selection */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Organization *
            </label>
            <SearchableDropdown
              options={organizations}
              value={formData.organisation_id}
              onChange={(value) => handleInputChange('organisation_id', value)}
              placeholder="Search organizations..."
              label=""
            />
            {errors.organisation_id && (
              <p className="text-red-500 text-xs mt-1">{errors.organisation_id}</p>
            )}
          </div>

          {/* Access Type */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Access Type *
            </label>
            <select
              value={formData.access_type}
              onChange={(e) => handleInputChange('access_type', e.target.value)}
              className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                errors.access_type ? 'border-red-300' : 'border-gray-300'
              }`}
            >
              <option value="">Select access type...</option>
              <option value="viewer">Viewer</option>
              <option value="organizer">Organizer</option>
              <option value="participant">Participant</option>
            </select>
            {errors.access_type && (
              <p className="text-red-500 text-xs mt-1">{errors.access_type}</p>
            )}
          </div>

          {/* Granted By */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Granted By
            </label>
            <input
              type="text"
              value={formData.granted_by || ''}
              onChange={(e) => handleInputChange('granted_by', e.target.value || null)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="Enter grantor (optional)"
            />
          </div>

          {/* Active Status */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Active Status
            </label>
            <div className="flex items-center space-x-3">
              <button
                type="button"
                onClick={() => handleInputChange('is_active', !formData.is_active)}
                className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                  formData.is_active ? 'bg-blue-600' : 'bg-gray-200'
                }`}
              >
                <span
                  className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ${
                    formData.is_active ? 'translate-x-5' : 'translate-x-0'
                  }`}
                />
              </button>
              <span className="text-sm text-gray-600">
                {formData.is_active ? 'Active' : 'Inactive'}
              </span>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex justify-end space-x-3 pt-4">
            <button
              type="button"
              onClick={handleClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-md transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isSaving}
              className="px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 disabled:bg-blue-400 rounded-md transition-colors"
            >
              {isSaving ? 'Saving...' : 'Add Data'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AddTournamentAccessModal;
